import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  message,
} from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { getErrorMessage } from "@/utilities";
import { confirmFooter } from "@/components/modals/confirmFooter";
import {
  PlannerBranch,
  PlannerConnection as Connection,
  deletePlannerConnection,
  getPlannerBranches,
  getPlannerConnection,
  savePlannerConnection,
} from "@/services/planner.api";

/**
 * La conexión del seller con Lepton Planner (xcons-docs#542, Fase 9): cada optimización comprada
 * aparece en el Planner del taller, y cuando el taller corta, la entrega avanza sola.
 *
 * Va en su propio form, con sus botones: guardarla valida las credenciales contra Lepton, y no
 * tiene que mezclarse con el "Guardar" de la configuración del optimizador.
 *
 * La contraseña y el token no se muestran nunca. Al editar, vacíos = no cambian.
 */

/** Los mismos estilos de grilla que el resto de la pantalla. */
const GRILLA: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  columnGap: 24,
  rowGap: 16,
  alignItems: "start",
};
const CELDA: React.CSSProperties = { marginBottom: 0 };

/**
 * Inputs comunes, sin autocompletado: con `Input.Password`, Chrome completaba la contraseña con
 * una guardada aunque tuviera `autoComplete="off"` —pasó con el usuario de Lepton del optimizador
 * (xcons-react-admin!943)—. Los `data-*` lo apagan en los gestores que no respetan `autoComplete`.
 */
const SIN_AUTOCOMPLETAR = {
  autoComplete: "off",
  spellCheck: false,
  "data-lpignore": "true",
  "data-1p-ignore": "true",
  "data-bwignore": "true",
  "data-form-type": "other",
} as const;

const fecha = (iso: string) =>
  new Date(iso).toLocaleString("es-AR", {
    dateStyle: "short",
    timeStyle: "short",
  });

export const PlannerConnection: React.FC<{ sellerCode: string }> = ({
  sellerCode,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [connection, setConnection] = useState<Connection | null>(null);
  const [branches, setBranches] = useState<PlannerBranch[]>([]);

  const load = (c: Connection) => {
    setConnection(c);
    form.resetFields();
    form.setFieldsValue({
      email: c.email,
      password: c.password,
      apiToken: c.apiToken ?? undefined,
      branchId: c.branchId ?? undefined,
    });
    setBranches(
      c.branchId
        ? [
            {
              id: c.branchId,
              name: c.branchName ?? `Sucursal ${c.branchId}`,
              hasEdgebanding: false,
            },
          ]
        : [],
    );
  };

  useEffect(() => {
    setLoading(true);
    getPlannerConnection(sellerCode)
      .then(load)
      .catch((e) => message.error(getErrorMessage(e)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerCode]);

  const credentials = () => {
    const v = form.getFieldsValue(true);
    return {
      email: (v.email ?? "").trim(),
      password: v.password || undefined,
      apiToken: v.apiToken?.trim() || undefined,
    };
  };

  const fetchBranches = async () => {
    try {
      await form.validateFields([
        "email",
        ...(connection?.connected ? [] : ["password"]),
      ]);
    } catch {
      return;
    }
    setBusy(true);
    try {
      const list = await getPlannerBranches(sellerCode, credentials());
      setBranches(list);
      if (list.length === 1) form.setFieldValue("branchId", list[0].id);
      else if (!list.some((b) => b.id === form.getFieldValue("branchId"))) {
        form.setFieldValue("branchId", undefined);
      }
      if (!list.length)
        message.warning("La cuenta de Lepton no tiene sucursales");
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }
    setBusy(true);
    try {
      load(
        await savePlannerConnection(sellerCode, {
          ...credentials(),
          branchId: Number(form.getFieldValue("branchId")),
        }),
      );
      message.success("Conexión con Planner guardada");
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await deletePlannerConnection(sellerCode);
      load({ sellerCode, enabled: true, connected: false });
      message.success("Se desconectó Planner");
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  /** Como "Quitar la cuenta de Lepton": "Atrás" a la izquierda y "Sí, …" primario a la derecha. */
  const confirmDisconnect = () =>
    Modal.confirm({
      title: "¿Desconectar Planner?",
      content:
        "Los pedidos nuevos dejan de aparecer en Planner y las entregas dejan de avanzar solas. Lo que ya está en Planner queda como está.",
      okText: "Sí, desconectar",
      cancelText: "Atrás",
      cancelButtonProps: { icon: <LeftOutlined /> },
      footer: confirmFooter,
      onOk: disconnect,
    });

  if (loading) {
    return (
      <Flex justify="center" style={{ padding: 24 }}>
        <Spin />
      </Flex>
    );
  }

  const connected = Boolean(connection?.connected);

  return (
    <Form form={form} layout="vertical" disabled={busy}>
      <Flex vertical gap={16}>
        {connected && connection?.lastAuthErrorAt && (
          <Alert
            type="error"
            showIcon
            message={`No se pudo entrar a Lepton desde el ${fecha(
              connection.lastAuthErrorAt,
            )}`}
            description="Revisá el email y la contraseña: mientras tanto, los pedidos no llegan a Planner."
          />
        )}
        {connected && connection?.xconsPermissionOk === false && (
          <Alert
            type="warning"
            showIcon
            message="La integración no tiene permiso para mover entregas"
            description="Los pedidos llegan a Planner, pero las entregas no avanzan solas hasta que XCONS configure el usuario de integración de este seller."
          />
        )}

        <div style={GRILLA}>
          <Form.Item
            label="Email de Lepton"
            name="email"
            style={CELDA}
            rules={[
              {
                required: true,
                type: "email",
                message: "Ingresá el email con el que se entra a Lepton",
              },
            ]}
          >
            <Input {...SIN_AUTOCOMPLETAR} />
          </Form.Item>
          <Form.Item
            label="Contraseña de Lepton"
            name="password"
            style={CELDA}
            rules={
              connected
                ? []
                : [{ required: true, message: "Ingresá la contraseña" }]
            }
            extra={
              connected
                ? "La que está guardada. Para cambiarla, reemplazala y guardá."
                : undefined
            }
          >
            <Input {...SIN_AUTOCOMPLETAR} />
          </Form.Item>
          <Form.Item
            label="Token de API (opcional)"
            name="apiToken"
            style={CELDA}
            extra={
              connection?.hasApiToken
                ? "El que está guardado. Para cambiarlo, reemplazalo y guardá."
                : "El de la pantalla API Tokens de Lepton. Sin él se entra con el email y la contraseña."
            }
          >
            <Input {...SIN_AUTOCOMPLETAR} />
          </Form.Item>
          <Form.Item
            label="Sucursal"
            style={CELDA}
            extra="La sucursal de Planner donde se cargan los pedidos."
          >
            <Flex gap={8}>
              <Form.Item
                name="branchId"
                noStyle
                rules={[{ required: true, message: "Elegí la sucursal" }]}
              >
                <Select
                  style={{ flex: 1, minWidth: 0 }}
                  placeholder="Traé las sucursales"
                  options={branches.map((b) => ({
                    value: b.id,
                    label: b.name,
                  }))}
                />
              </Form.Item>
              <Button onClick={fetchBranches} loading={busy}>
                Traer sucursales
              </Button>
            </Flex>
          </Form.Item>
        </div>

        <Flex justify="flex-end" gap={8}>
          {connected && (
            <Button onClick={confirmDisconnect} disabled={busy}>
              Desconectar Planner
            </Button>
          )}
          <Button type="primary" onClick={save} loading={busy}>
            {connected ? "Guardar conexión" : "Conectar"}
          </Button>
        </Flex>
      </Flex>
    </Form>
  );
};
